import React, { useState, useEffect } from 'react';
import { View, Text, TextInput, Pressable, Modal, StyleSheet } from 'react-native';
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';

interface AgeRangeEditModalProps {
    visible: boolean;
    onClose: () => void;
    currentStartAge: string | number;
    currentEndAge: string | number;
    onSave: (startAge: string, endAge: string) => void;
    sessionTitle?: string;
}

const AgeRangeEditModal: React.FC<AgeRangeEditModalProps> = ({
    visible,
    onClose,
    currentStartAge,
    currentEndAge,
    onSave,
    sessionTitle = "Edit Age Range"
}) => {
    const [startAge, setStartAge] = useState(currentStartAge?.toString() || '');
    const [endAge, setEndAge] = useState(currentEndAge?.toString() || '');
    const [error, setError] = useState('');

    // Update state when modal opens or current values change
    useEffect(() => {
        if (visible) {
            setStartAge(currentStartAge?.toString() || '');
            setEndAge(currentEndAge?.toString() || '');
            setError('');
        }
    }, [visible, currentStartAge, currentEndAge]);

    const validateAgeRange = (start: string, end: string) => {
        if (!start.trim() || !end.trim()) {
            return 'Both minimum and maximum ages are required';
        }
        const startNum = parseInt(start);
        const endNum = parseInt(end);
        if (isNaN(startNum) || isNaN(endNum)) {
            return 'Ages must be valid numbers';
        }
        if (startNum < 1 || endNum < 1) {
            return 'Ages must be greater than 0';
        }
        if (startNum > 100 || endNum > 100) {
            return 'Ages cannot exceed 100';
        }
        if (startNum >= endNum) {
            return 'Minimum age must be less than maximum age';
        }
        return '';
    };

    const handleStartAgeChange = (text: string) => {
        const numericText = text.replace(/[^0-9]/g, '');
        setStartAge(numericText);
        setError(validateAgeRange(numericText, endAge));
    };

    const handleEndAgeChange = (text: string) => {
        const numericText = text.replace(/[^0-9]/g, '');
        setEndAge(numericText);
        setError(validateAgeRange(startAge, numericText));
    };

    const isValid = !error && startAge.trim() !== '' && endAge.trim() !== '';

    const handleSave = () => {
        const validationError = validateAgeRange(startAge, endAge);
        if (validationError) {
            setError(validationError);
            return;
        }
        onSave(startAge, endAge);
        onClose();
    };

    const handleCancel = () => {
        setStartAge(currentStartAge?.toString() || '');
        setEndAge(currentEndAge?.toString() || '');
        onClose();
    };

    return (
        <Modal
            visible={visible}
            transparent={true}
            animationType="fade"
            onRequestClose={onClose}
        >
            <View style={styles.overlay}>
                <View style={styles.modalContainer}>
                    {/* Header */}
                    <View style={styles.header}>
                        <Text style={styles.title}>{sessionTitle}</Text>
                        <Pressable onPress={onClose} style={styles.closeButton}>
                            <MaterialCommunityIcons name="close" size={20} color="#666" />
                        </Pressable>
                    </View>

                    {/* Content */}
                    <View style={styles.content}>
                        <Text style={styles.label}>Age Range</Text>
                        
                        <View style={styles.ageInputsContainer}>
                            <View style={styles.ageInputGroup}>
                                <Text style={styles.ageLabel}>Minimum Age</Text>
                                <TextInput
                                    style={[styles.ageInput, error ? styles.ageInputError : null]}
                                    value={startAge}
                                    onChangeText={handleStartAgeChange}
                                    placeholder="5"
                                    placeholderTextColor="#adb5bd"
                                    keyboardType="numeric"
                                    autoFocus={true}
                                    selectTextOnFocus={true}
                                />
                                <Text style={styles.yearText}>years old</Text>
                            </View>
                            
                            <View style={styles.ageInputGroup}>
                                <Text style={styles.ageLabel}>Maximum Age</Text>
                                <TextInput
                                    style={[styles.ageInput, error ? styles.ageInputError : null]}
                                    value={endAge}
                                    onChangeText={handleEndAgeChange}
                                    placeholder="12"
                                    placeholderTextColor="#adb5bd"
                                    keyboardType="numeric"
                                    selectTextOnFocus={true}
                                />
                                <Text style={styles.yearText}>years old</Text>
                            </View>
                        </View>
                        
                        {error ? (
                            <Text style={styles.errorText}>{error}</Text>
                        ) : (
                            <Text style={styles.helperText}>
                                Set the age range for participants in this session
                            </Text>
                        )}
                    </View>

                    {/* Footer */}
                    <View style={styles.footer}>
                        <Pressable onPress={handleCancel} style={[styles.button, styles.cancelButton]}>
                            <Text style={styles.cancelButtonText}>Cancel</Text>
                        </Pressable>
                        <Pressable 
                            onPress={handleSave} 
                            style={[styles.button, isValid ? styles.saveButton : styles.saveButtonDisabled]}
                            disabled={!isValid}
                        >
                            <Text style={[styles.saveButtonText, !isValid && styles.saveButtonTextDisabled]}>Save</Text>
                        </Pressable>
                    </View>
                </View>
            </View>
        </Modal>
    );
};

const styles = StyleSheet.create({
    overlay: {
        flex: 1,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 20,
    },
    modalContainer: {
        backgroundColor: 'white',
        borderRadius: 12,
        width: '100%',
        maxWidth: 400,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.25,
        shadowRadius: 12,
        elevation: 8,
    },
    header: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingHorizontal: 20,
        paddingVertical: 16,
        borderBottomWidth: 1,
        borderBottomColor: '#e9ecef',
    },
    title: {
        fontSize: 18,
        fontWeight: '600',
        color: '#333',
    },
    closeButton: {
        padding: 4,
    },
    content: {
        padding: 20,
    },
    label: {
        fontSize: 16,
        fontWeight: '500',
        color: '#333',
        marginBottom: 16,
        textAlign: 'center',
    },
    ageInputsContainer: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        gap: 16,
    },
    ageInputGroup: {
        flex: 1,
        alignItems: 'center',
    },
    ageLabel: {
        fontSize: 14,
        fontWeight: '500',
        color: '#666',
        marginBottom: 8,
    },
    ageInput: {
        borderWidth: 1,
        borderColor: '#dee2e6',
        borderRadius: 12,
        padding: 16,
        fontSize: 18,
        textAlign: 'center',
        backgroundColor: '#ffffff',
        width: '100%',
        minHeight: 52,
        color: '#212529',
    },
    ageInputError: {
        borderColor: '#dc3545',
        backgroundColor: '#fff5f5',
    },
    yearText: {
        fontSize: 12,
        color: '#666',
        marginTop: 6,
    },
    helperText: {
        fontSize: 12,
        color: '#666',
        marginTop: 16,
        textAlign: 'center',
    },
    errorText: {
        fontSize: 12,
        color: '#dc3545',
        marginTop: 16,
        textAlign: 'center',
        fontWeight: '500',
    },
    footer: {
        flexDirection: 'row',
        padding: 20,
        paddingTop: 0,
        gap: 12,
    },
    button: {
        flex: 1,
        paddingVertical: 12,
        borderRadius: 8,
        alignItems: 'center',
    },
    cancelButton: {
        backgroundColor: '#f8f9fa',
        borderWidth: 1,
        borderColor: '#e9ecef',
    },
    cancelButtonText: {
        fontSize: 16,
        fontWeight: '500',
        color: '#666',
    },
    saveButton: {
        backgroundColor: '#007bff',
    },
    saveButtonDisabled: {
        backgroundColor: '#cccccc',
    },
    saveButtonText: {
        fontSize: 16,
        fontWeight: '500',
        color: 'white',
    },
    saveButtonTextDisabled: {
        color: '#666666',
    },
});

export default AgeRangeEditModal; 